import { Space, Typography } from 'antd';
import { message } from '@/components/Message';
import React, { useMemo } from 'react';
import { PostInvalidate } from '../../../typings/global';
import {
  AppointmentHelper,
  AppointmentStatus,
  AppointmentStatusMap,
  updateStatus,
} from '@/models/appointment';
import { OrderDetail } from '@/models/order';
import Modal from '../Modal';
import PosButton from '../PosButton';
import { CheckOutlined, PoweroffOutlined, RetweetOutlined } from '@ant-design/icons';

type Props = {
  orderDetail: OrderDetail;
  updateOrderDetail: (f: (draft: OrderDetail) => void | OrderDetail) => void;
} & PostInvalidate<OrderDetail>;

const AppointmentOperation: React.FC<Props> = ({
  orderDetail,
  updateOrderDetail,
  postInvalidate,
}: Props) => {
  const { order, appointment, user } = orderDetail;

  const changeStatus = async (status: AppointmentStatus) =>
    new Promise<void>(resolve => {
      Modal.confirm({
        title: '二次确认',
        content: (
          <>
            将变更预约单状态为:{' '}
            <Typography.Text strong>{AppointmentStatusMap[status].desc}</Typography.Text>
          </>
        ),
        okText: '确认',
        onOk: async () => {
          await updateStatus({ appointment, newStatus: status });
          message.success('操作成功');
          updateOrderDetail(draft => {
            draft.appointment.status = status;
          });
          resolve();
        },
        onCancel: resolve,
      });
    });

  const statusAction = useMemo(() => {
    const checkIn =
      !order && AppointmentHelper.canCheckIn(appointment) ? (
        <PosButton
          key={'checkIn'}
          icon={<RetweetOutlined />}
          onClick={async () => {
            await changeStatus(AppointmentStatus.CHECKIN);
          }}>
          到店
        </PosButton>
      ) : null;
    const complete =
      !order && AppointmentHelper.canComplete(appointment) ? (
        <PosButton
          key={'complete'}
          icon={<CheckOutlined />}
          onClick={async () => {
            await changeStatus(AppointmentStatus.COMPLETE);
          }}>
          完成
        </PosButton>
      ) : null;
    const cancel = AppointmentHelper.canCancel(appointment) ? (
      <PosButton
        danger
        key={'cancel'}
        icon={<PoweroffOutlined />}
        onClick={async () => {
          await changeStatus(AppointmentStatus.CANCELED);
        }}>
        取消
      </PosButton>
    ) : null;

    return [cancel].filter(Boolean);
  }, [order, appointment]);

  return <Space wrap>{statusAction}</Space>;
};

export default AppointmentOperation;
